/**
 * Redirect safety.
 *
 * After signing in, the user is returned to wherever they were headed. That
 * destination arrives in the URL, which means an attacker can put anything they
 * like in it. A login page that forwards to an arbitrary address is a phishing
 * tool wearing the customer's branding: the victim really did sign in to the
 * real site, and the real site really did send them onward.
 *
 * So the rule is an allowlist of shapes, not a blocklist of tricks. Anything
 * that is not obviously an internal path is replaced with the default.
 */

export const DEFAULT_REDIRECT = '/'

/**
 * Paths a signed-in user may be sent to after login.
 *
 * Prefix matching, because these have child routes. Kept explicit rather than
 * "anything starting with a slash", so a route added later has to be considered
 * rather than inherited.
 */
const ALLOWED_PREFIXES = [
  '/',
  '/dashboard',
  '/settings',
  '/wage-matrix',
  '/employee-costs',
  '/timesheets',
  '/jobs',
  '/unallocated',
  '/import',
  '/admin',
  '/platform',
]

/**
 * True when `candidate` is a safe internal path.
 *
 * Rejected, and each of these is a real technique rather than a hypothetical:
 *
 *   https://evil.example        absolute URL to another origin
 *   //evil.example              protocol relative, inherits the current scheme
 *   /\evil.example              backslash, which some parsers treat as a slash
 *   javascript:alert(1)         scheme that executes rather than navigates
 *   /%2f%2fevil.example         encoded slashes, decoded later by something else
 *   /dashboard\n/x              control characters, used to split or confuse
 */
/**
 * True when the string contains a C0 control character or DEL.
 *
 * Written as a code-point scan rather than a regular expression range, because
 * a literal control character inside a pattern is unreadable in review and the
 * linter is right to object to it.
 */
function hasControlCharacter(value: string): boolean {
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0
    if (code < 0x20 || code === 0x7f) return true
  }
  return false
}

export function isSafeRedirect(candidate: string | null | undefined): boolean {
  if (typeof candidate !== 'string') return false
  if (candidate === '') return false

  // Decode first. A check that passes on the encoded form and fails on the
  // decoded one protects nothing, because the browser decodes before it
  // navigates. A malformed encoding is itself disqualifying.
  let decoded: string
  try {
    decoded = decodeURIComponent(candidate)
  } catch {
    return false
  }

  for (const value of [candidate, decoded]) {
    if (!value.startsWith('/')) return false
    if (value.startsWith('//')) return false
    if (value.startsWith('/\\')) return false
    if (hasControlCharacter(value)) return false
    if (/^\/+[a-z][a-z0-9+.-]*:/i.test(value)) return false
    if (value.includes('\\')) return false
  }

  const path = decoded.split(/[?#]/)[0] ?? ''
  return ALLOWED_PREFIXES.some(
    (prefix) => path === prefix || (prefix !== '/' && path.startsWith(`${prefix}/`)),
  )
}

/** The candidate if it is safe, otherwise the default. Never throws. */
export function safeRedirectOrDefault(candidate: string | null | undefined): string {
  return isSafeRedirect(candidate) ? (candidate as string) : DEFAULT_REDIRECT
}
